import type { ComponentProps } from 'react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Flex,
  FormGroup,
  FormControl,
  FormControlText,
  Input,
  InputCheckBox
} from '@itandi/itandi-bb-ui';
import { SingleComboBox } from '@itandi/itandi-bb-ui/SingleComboBox';
import { Tooltip } from '@itandi/itandi-bb-ui/Tooltip';
import type { DealSearchCondition } from '~/models/Deal';
import type { CompanyVendor } from '~/models/CompanyVendor';
import { convertToComboBoxOption } from '~/models/CompanyVendor';
import { useSubtypes } from '~/hooks/useSubtypes';
import { DealTypeCheckBox } from './detail/DealTypeCheckBox';
import { SearchConditionDate } from './detail/SearchConditionDate';
import { CompanyStoreCombobox } from './detail/CompanyStoreCombobox';
import { StaffCombobox } from './detail/StaffCombobox';
import useItandiAccount from '~/hooks/useItandiAccount';
import { StatusCombobox } from './detail/StatusCombobox';
import { useStatuses } from '~/hooks/useStatuses';
import { ApplicationTypeCheckBox } from '~/components/specifics/deals/index/search/detail/ApplicationTypeCheckBox';

type Props = {
  companyVendors: CompanyVendor[];
  selectedCompanyVendor: CompanyVendor | null;
  dealSearchCondition: DealSearchCondition;
};

export const DealSearchDetailTab: React.FC<Props> = ({
  companyVendors,
  selectedCompanyVendor,
  dealSearchCondition
}: Props) => {
  const { data: subtypes } = useSubtypes().useIndex();
  const { register, setValue } = useFormContext<DealSearchCondition>();
  const defaultPrioritySort = dealSearchCondition.prioritySort ?? true;
  const [isPrioritySort, setIsPrioritySort] =
    React.useState(defaultPrioritySort);
  const { data: session } = useItandiAccount().currentAccount();
  const { data: statuses } = useStatuses().index();
  const onChangeCompanyVendor = React.useCallback(
    async (
      option: ComponentProps<typeof SingleComboBox>['value'] | null
    ): Promise<void> => {
      setValue('companyVendorId', option ? Number(option.value) : undefined);
    },
    [setValue]
  );
  if (subtypes === null || session === null || statuses === null) {
    return null;
  }
  return (
    <FormGroup variant="vertical">
      <FormControl label="未読を優先表示する">
        <InputCheckBox
          checked={isPrioritySort}
          label="未読を優先表示する"
          onChange={(e): void => {
            setValue('prioritySort', e.target.checked);
            setIsPrioritySort(e.target.checked);
          }}
        />
      </FormControl>
      <FormControl label="状況(ステータス)">
        <StatusCombobox
          dealSearchCondition={dealSearchCondition}
          statuses={statuses}
        />
      </FormControl>
      <FormControl label="状況(申請関連)">
        <Flex marginY={8} wrap="wrap">
          <ApplicationTypeCheckBox
            key="cancel"
            applicationType="cancel"
            dealSearchCondition={dealSearchCondition}
            label="取下申請中"
          />
          <ApplicationTypeCheckBox
            key="requestEstimation"
            applicationType="requestEstimation"
            dealSearchCondition={dealSearchCondition}
            label="見積申請あり"
          />
          <ApplicationTypeCheckBox
            key="requestEstimationByCompany"
            applicationType="requestEstimationByCompany"
            dealSearchCondition={dealSearchCondition}
            label="見積申請中"
          />
          <Flex alignItems="center">
            <Tooltip>
              管理会社にて見積作成を行う案件のうち、見積変更を「申請中」の条件で絞り込みます
            </Tooltip>
          </Flex>
          <ApplicationTypeCheckBox
            key="draftEstimationByCompany"
            applicationType="draftEstimationByCompany"
            dealSearchCondition={dealSearchCondition}
            label="見積編集中"
          />
          <Flex alignItems="center">
            <Tooltip>
              管理会社にて見積作成を行う案件のうち、見積変更を「編集中」(未申請)の条件で絞り込みます
            </Tooltip>
          </Flex>
        </Flex>
      </FormControl>
      <FormControl label="案件タイプ">
        <Flex marginY={8} wrap="wrap">
          <DealTypeCheckBox
            key="restoration"
            dealSearchCondition={dealSearchCondition}
            dealType="restoration"
            label="原状回復"
          />
          <DealTypeCheckBox
            key="repair"
            dealSearchCondition={dealSearchCondition}
            dealType="repair"
            label="修繕"
          />
          {subtypes.map((subtype) => (
            <DealTypeCheckBox
              key={subtype.id}
              dealSearchCondition={dealSearchCondition}
              dealSubtypeId={subtype.id}
              dealType="other"
              label={subtype.name}
            />
          ))}
        </Flex>
      </FormControl>
      <FormControl label="物件名">
        <Input {...register('name')} placeholder="六本木マンション" />
      </FormControl>
      <FormControl label="部屋番号">
        <Input {...register('roomNumber')} placeholder="101" />
      </FormControl>
      <FormControl label="所在地">
        <Input {...register('address')} placeholder="東京都港区六本木1-2-3" />
      </FormControl>
      <FormControl label="入居者">
        <Input {...register('tenantName')} placeholder="内装 太郎" />
      </FormControl>
      {session.store?.masterAccount && (
        <FormControl label="店舗">
          <CompanyStoreCombobox dealSearchCondition={dealSearchCondition} />
        </FormControl>
      )}
      <FormControl label="担当者">
        <StaffCombobox dealSearchCondition={dealSearchCondition} />
      </FormControl>
      <FormControl label="施工会社">
        <SingleComboBox
          defaultValue={
            selectedCompanyVendor
              ? convertToComboBoxOption(selectedCompanyVendor)
              : null
          }
          onChange={onChangeCompanyVendor}
          options={companyVendors.map((c: CompanyVendor) =>
            convertToComboBoxOption(c)
          )}
          placeholder="施工会社"
        />
      </FormControl>
      <FormControl label="更新日">
        <Flex alignItems="center">
          <SearchConditionDate
            attributeName="updatedMinDate"
            defaultValue={dealSearchCondition.updatedMinDate}
          />
          〜
          <SearchConditionDate
            attributeName="updatedMaxDate"
            defaultValue={dealSearchCondition.updatedMaxDate}
          />
        </Flex>
      </FormControl>
      <FormControl label="立会日程">
        <Flex alignItems="center">
          <SearchConditionDate
            attributeName="inspectionMinDate"
            defaultValue={dealSearchCondition.inspectionMinDate}
          />
          〜
          <SearchConditionDate
            attributeName="inspectionMaxDate"
            defaultValue={dealSearchCondition.inspectionMaxDate}
          />
        </Flex>
      </FormControl>
      <FormControl label="現地調査日程">
        <Flex alignItems="center">
          <SearchConditionDate
            attributeName="surveyMinDate"
            defaultValue={dealSearchCondition.surveyMinDate}
          />
          〜
          <SearchConditionDate
            attributeName="surveyMaxDate"
            defaultValue={dealSearchCondition.surveyMaxDate}
          />
        </Flex>
      </FormControl>
      <FormControl label="見積提出日">
        <Flex alignItems="center">
          <SearchConditionDate
            attributeName="sendEstimateMinDate"
            defaultValue={dealSearchCondition.sendEstimateMinDate}
          />
          〜
          <SearchConditionDate
            attributeName="sendEstimateMaxDate"
            defaultValue={dealSearchCondition.sendEstimateMaxDate}
          />
        </Flex>
        <FormControlText help="ステータスを「見積確認中」に進めた日" />
      </FormControl>
      <FormControl label="発注日">
        <Flex alignItems="center">
          <SearchConditionDate
            attributeName="orderMinDate"
            defaultValue={dealSearchCondition.orderMinDate}
          />
          〜
          <SearchConditionDate
            attributeName="orderMaxDate"
            defaultValue={dealSearchCondition.orderMaxDate}
          />
        </Flex>
        <FormControlText help="ステータスを「工事完了待ち」に進めた日" />
      </FormControl>
      <FormControl label="工期開始日">
        <Flex alignItems="center">
          <SearchConditionDate
            attributeName="constructionMinStartDate"
            defaultValue={dealSearchCondition.constructionMinStartDate}
          />
          〜
          <SearchConditionDate
            attributeName="constructionMaxStartDate"
            defaultValue={dealSearchCondition.constructionMaxStartDate}
          />
        </Flex>
      </FormControl>
      <FormControl label="工期終了日">
        <Flex alignItems="center">
          <SearchConditionDate
            attributeName="constructionMinEndDate"
            defaultValue={dealSearchCondition.constructionMinEndDate}
          />
          〜
          <SearchConditionDate
            attributeName="constructionMaxEndDate"
            defaultValue={dealSearchCondition.constructionMaxEndDate}
          />
        </Flex>
      </FormControl>
      <FormControl label="検収依頼日">
        <Flex alignItems="center">
          <SearchConditionDate
            attributeName="constructionReportedMinDate"
            defaultValue={dealSearchCondition.constructionReportedMinDate}
          />
          〜
          <SearchConditionDate
            attributeName="constructionReportedMaxDate"
            defaultValue={dealSearchCondition.constructionReportedMaxDate}
          />
        </Flex>
        <FormControlText help="ステータスを「検収中」に進めた日" />
      </FormControl>
      <FormControl label="完了日">
        <Flex alignItems="center">
          <SearchConditionDate
            attributeName="completedMinDate"
            defaultValue={dealSearchCondition.completedMinDate}
          />
          〜
          <SearchConditionDate
            attributeName="completedMaxDate"
            defaultValue={dealSearchCondition.completedMaxDate}
          />
        </Flex>
        <FormControlText help="ステータスを「完了」に進めた日" />
      </FormControl>
      <FormControl label="登録日">
        <Flex alignItems="center">
          <SearchConditionDate
            attributeName="registrationMinDate"
            defaultValue={dealSearchCondition.registrationMinDate}
          />
          〜
          <SearchConditionDate
            attributeName="registrationMaxDate"
            defaultValue={dealSearchCondition.registrationMaxDate}
          />
        </Flex>
      </FormControl>
    </FormGroup>
  );
};
